import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Drawer, Form, Input, InputNumber, Modal, Radio, Select, Space, Typography, message } from 'antd';
import { useState } from 'react';
import { FaMobile } from 'react-icons/fa6';
import axios from '../config/axios';
import { Device } from '../types/device.types';
import { PaymentAccount } from '../types/payment-account.types';
import { handleAPIError } from '../utils/helpers';

interface RetailerPayoutProps {
	device: Device;
	open: boolean;
	onClose: () => void;
	onSuccess?: () => void;
}

interface PayoutFormValues {
	device_id: number;
	amount: number;
	notes?: string;
	type: 'record_only' | 'record_and_trx';
	payment_account_id?: number;
}

const { Title, Text } = Typography;
const { TextArea } = Input;

const RetailerPayout = ({ device, open, onClose, onSuccess }: RetailerPayoutProps) => {
	const [form] = Form.useForm<PayoutFormValues>();
	const [showConfirmModal, setShowConfirmModal] = useState(false);
	const [pendingFormValues, setPendingFormValues] = useState<PayoutFormValues | null>(null);
	const queryClient = useQueryClient();

	// Calculate max payable amount
	const maxPayableAmount = parseFloat(device.phone_amount) - parseFloat(device.cash_in_hand) - parseFloat(device.zf_paid_to_retailer);

	// Fetch retailer payment accounts
	const { data: accountsData, isLoading: isLoadingAccounts } = useQuery<{ results: PaymentAccount[] }>({
		queryKey: ['retailerPaymentAccounts', device.retailer.id],
		queryFn: async () => {
			const { data } = await axios.get(`/admin/retailers/${device.sold_by!.id}/payment-accounts`, { withCredentials: true });
			return data;
		},
		enabled: open, // Only fetch when drawer is open
	});

	// Filter for only mobile_money accounts as per requirements
	const mobileMoneAccounts = accountsData?.results.filter((account) => account.type === 'mobile_money' && account.is_active) || [];

	// Create payout mutation
	const { mutate: createPayout, isPending } = useMutation({
		mutationFn: async (values: PayoutFormValues) => {
			const { data } = await axios.post('/admin/payments/payouts', values, { withCredentials: true });
			return data;
		},
		onSuccess: () => {
			message.success('Payout created successfully');
			form.resetFields();
			queryClient.invalidateQueries({ queryKey: ['devices'] });
			queryClient.invalidateQueries({ queryKey: ['retailers'] });
			if (onSuccess) {
				onSuccess();
			}
			onClose();
		},
		onError: (error) => {
			handleAPIError(error as Error);
		},
	});

	const handleSubmit = (values: PayoutFormValues) => {
		// Store form values and show confirmation modal
		setPendingFormValues(values);
		setShowConfirmModal(true);
	};

	const handleConfirmPayout = () => {
		if (!pendingFormValues) return;

		// Add device_id to the form values
		const payoutData = {
			...pendingFormValues,
			device_id: device.id,
		};
		// Remove payment_account_id if type is record_only
		if (pendingFormValues.type === 'record_only') {
			delete payoutData.payment_account_id;
		}
		createPayout(payoutData);
		setShowConfirmModal(false);
		setPendingFormValues(null);
	};

	const handleClose = () => {
		form.resetFields();
		setShowConfirmModal(false);
		setPendingFormValues(null);
		onClose();
	};

	const handleCancelConfirmation = () => {
		setShowConfirmModal(false);
		setPendingFormValues(null);
	};

	return (
		<>
			<Drawer
				title="Create Retailer Payout"
				placement="right"
				onClose={handleClose}
				open={open}
				width={500}
				destroyOnClose
			>
				<div className="space-y-6">
					{/* Device Information */}
					<div className="rounded-lg bg-gray-50 p-4">
						<Title
							level={5}
							className="mb-3"
						>
							Device Information
						</Title>
						<div className="space-y-2 text-sm">
							<div className="flex justify-between">
								<Text className="text-gray-600">Device ID:</Text>
								<Text>{device.id}</Text>
							</div>
							<div className="flex justify-between">
								<Text className="text-gray-600">Phone Model:</Text>
								<Text>{device.phone_model}</Text>
							</div>
							<div className="flex justify-between">
								<Text className="text-gray-600">Sold by:</Text>
								<Text>{device.sold_by?.business_name}</Text>
							</div>
						</div>
					</div>

					{/* Payment Calculation */}
					<div className="rounded-lg bg-blue-50 p-4">
						<Title
							level={5}
							className="mb-3 text-blue-800"
						>
							Payment Calculation
						</Title>
						<div className="space-y-2 text-sm">
							<div className="flex justify-between">
								<Text className="text-gray-600">Phone Amount:</Text>
								<Text>UGX {parseFloat(device.phone_amount).toLocaleString()}</Text>
							</div>
							<div className="flex justify-between">
								<Text className="text-gray-600">Cash in Hand:</Text>
								<Text>UGX {parseFloat(device.cash_in_hand).toLocaleString()}</Text>
							</div>
							<div className="flex justify-between">
								<Text className="text-gray-600">ZF Paid to Retailer:</Text>
								<Text>UGX {parseFloat(device.zf_paid_to_retailer).toLocaleString()}</Text>
							</div>
							<hr className="my-2" />
							<div className="flex justify-between font-semibold">
								<Text className="text-blue-800">Max Payable Amount:</Text>
								<Text className="text-blue-800">UGX {maxPayableAmount.toLocaleString()}</Text>
							</div>
						</div>
					</div>

					{/* Payout Form */}
					<Form
						form={form}
						layout="vertical"
						onFinish={handleSubmit}
						requiredMark={false}
						initialValues={{
							type: 'record_only',
						}}
					>
						<Form.Item
							name="type"
							label="Payout Type"
							rules={[{ required: true, message: 'Please select payout type' }]}
						>
							<Radio.Group>
								<Space direction="vertical">
									<Radio value="record_only">Record Only (manual payment)</Radio>
									<Radio value="record_and_trx">Record and Transaction (system payment)</Radio>
								</Space>
							</Radio.Group>
						</Form.Item>

						<Form.Item shouldUpdate={(prevValues, currentValues) => prevValues.type !== currentValues.type}>
							{({ getFieldValue }) => {
								const payoutType = getFieldValue('type');

								// Show warning only if user selected record_and_trx but no accounts available
								if (payoutType === 'record_and_trx' && mobileMoneAccounts.length === 0 && !isLoadingAccounts) {
									return (
										<div className="rounded-lg bg-yellow-50 p-6 text-center">
											<Text className="text-yellow-700">
												No active mobile money accounts found for this retailer. Please ensure the retailer has added an
												active mobile money payment account for system payments.
											</Text>
										</div>
									);
								}
								return null;
							}}
						</Form.Item>

						<Form.Item shouldUpdate={(prevValues, currentValues) => prevValues.type !== currentValues.type}>
							{({ getFieldValue }) => {
								const payoutType = getFieldValue('type');
								return payoutType === 'record_and_trx' ? (
									<Form.Item
										name="payment_account_id"
										label="Payment Account"
										rules={[{ required: true, message: 'Please select a payment account for system payment' }]}
									>
										<Select
											loading={isLoadingAccounts}
											placeholder="Select mobile money account"
											optionLabelProp="label"
										>
											{mobileMoneAccounts.map((account) => (
												<Select.Option
													key={account.id}
													value={account.id}
													label={account.account_number}
												>
													<div className="flex items-center gap-2">
														<FaMobile className="text-purple-600" />
														<span>{account.account_number}</span>
														<span className="text-gray-500">({account.bank_name})</span>
													</div>
												</Select.Option>
											))}
										</Select>
									</Form.Item>
								) : null;
							}}
						</Form.Item>

						<Form.Item
							name="amount"
							label={'Amount'}
							rules={[
								{ required: true, message: 'Please enter payout amount' },
								{
									validator: (_, value) => {
										if (value > maxPayableAmount) {
											return Promise.reject(
												`Amount cannot exceed max payable amount of UGX ${maxPayableAmount.toLocaleString()}`,
											);
										}
										if (value <= 0) {
											return Promise.reject('Amount must be greater than zero');
										}
										return Promise.resolve();
									},
								},
							]}
						>
							<InputNumber
								style={{ width: '100%' }}
								placeholder="Enter payout amount"
								min={1}
								max={maxPayableAmount}
								formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
								parser={(value: string | undefined) => {
									if (!value) return 0;
									return Number(value.replace(/\$\s?|(,*)/g, ''));
								}}
								addonBefore="UGX"
							/>
						</Form.Item>

						<div className="mb-4">
							<p className="mb-2 text-sm text-gray-500">Quick select:</p>
							<Button
								onClick={() => {
									form.setFieldsValue({ amount: maxPayableAmount });
								}}
								disabled={maxPayableAmount <= 0}
								size="small"
								type="dashed"
							>
								Max Amount (UGX {maxPayableAmount.toLocaleString()})
							</Button>
						</div>

						<Form.Item
							name="notes"
							label="Notes (Optional)"
						>
							<TextArea
								placeholder="Add any notes about this payout..."
								rows={3}
							/>
						</Form.Item>

						<Form.Item shouldUpdate>
							{({ getFieldValue }) => {
								const payoutType = getFieldValue('type');
								const needsPaymentAccount = payoutType === 'record_and_trx';
								const hasRequiredAccounts = !needsPaymentAccount || mobileMoneAccounts.length > 0;

								return (
									<div className="flex justify-end space-x-3 pt-4">
										<Button onClick={handleClose}>Cancel</Button>
										<Button
											type="primary"
											htmlType="submit"
											loading={isPending}
											disabled={isPending || isLoadingAccounts || !hasRequiredAccounts || maxPayableAmount <= 0}
										>
											Create Payout
										</Button>
									</div>
								);
							}}
						</Form.Item>
					</Form>

					{maxPayableAmount <= 0 && (
						<div className="rounded-lg bg-red-50 p-6 text-center">
							<Text className="text-red-700">
								No amount available for payout. The maximum payable amount is UGX {maxPayableAmount.toLocaleString()}.
							</Text>
						</div>
					)}
				</div>
			</Drawer>

			{/* Confirmation Modal */}
			<Modal
				title="Confirm Payout"
				open={showConfirmModal}
				onCancel={handleCancelConfirmation}
				footer={[
					<Button
						key="cancel"
						onClick={handleCancelConfirmation}
					>
						Cancel
					</Button>,
					<Button
						key="confirm"
						type="primary"
						loading={isPending}
						onClick={handleConfirmPayout}
						disabled={isPending}
					>
						Confirm Payout
					</Button>,
				]}
				destroyOnClose
			>
				{pendingFormValues && (
					<div className="space-y-4">
						<div className="rounded-lg bg-blue-50 p-4">
							<Title
								level={5}
								className="mb-3 text-blue-800"
							>
								Payout Summary
							</Title>
							<div className="space-y-2 text-sm">
								<div className="flex justify-between">
									<Text className="text-gray-600">Device ID:</Text>
									<Text>{device.id}</Text>
								</div>
								<div className="flex justify-between">
									<Text className="text-gray-600">Phone Model:</Text>
									<Text>{device.phone_model}</Text>
								</div>
								<div className="flex justify-between">
									<Text className="text-gray-600">Sold by:</Text>
									<Text>{device.sold_by?.business_name}</Text>
								</div>
								<div className="flex justify-between">
									<Text className="text-gray-600">Payout Type:</Text>
									<Text>
										{pendingFormValues.type === 'record_only'
											? 'Record Only (manual payment)'
											: 'Record and Transaction (system payment)'}
									</Text>
								</div>
								{pendingFormValues.type === 'record_and_trx' && pendingFormValues.payment_account_id && (
									<div className="flex justify-between">
										<Text className="text-gray-600">Payment Account:</Text>
										<Text>
											{mobileMoneAccounts.find((acc) => acc.id === pendingFormValues.payment_account_id)?.account_number}
										</Text>
									</div>
								)}
								<div className="flex justify-between font-semibold">
									<Text className="text-blue-800">Amount:</Text>
									<Text className="text-blue-800">UGX {pendingFormValues.amount.toLocaleString()}</Text>
								</div>
								{pendingFormValues.notes && (
									<div>
										<Text className="text-gray-600">Notes:</Text>
										<Text className="block text-gray-800">{pendingFormValues.notes}</Text>
									</div>
								)}
							</div>
						</div>
						<div className="rounded-lg bg-yellow-50 p-4">
							<Text className="font-medium text-yellow-800">⚠️ Please review all details carefully before confirming this payout.</Text>
						</div>
					</div>
				)}
			</Modal>
		</>
	);
};

export default RetailerPayout;
